'use client';

import { forwardRef, useId, useMemo, useState } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableValue } from '../../hooks/use-controllable-value';
import { useMediaQuery } from '../../hooks/use-media-query';
import { FormFieldContext } from '../form-field/context/form-field.context';
import { DateTimeBlockRootContext } from './context/date-time-block-root.context';
import type { DateTimeBlockRootPropsInterface } from './types/date-time-block-root.types';

/**
 * `DateTimeBlock.Root` is the container component that provides context
 * for all `DateTimeBlock` sub-components.
 *
 * @since 10.2.0
 */
export const DateTimeBlockRoot = forwardRef<HTMLDivElement, DateTimeBlockRootPropsInterface>(
  (props: DateTimeBlockRootPropsInterface, forwardedRef) => {
    const {
      children,
      className,
      css: cssProp,
      dates,
      datesToShow,
      defaultSelectedDate,
      defaultSelectedTime,
      disabledDates,
      disabledTimes,
      timeDisplay = 'select',
      label = 'Select a date and time',
      multiple,
      onDateChange,
      onTimeChange,
      selectedDate,
      selectedTime,
      times,
      touchScreenOffset,
      ...rest
    } = props;

    const [selectedDateState, setSelectedDateState] = useControllableValue<
      Date | string | undefined
    >({
      controlledValue: selectedDate,
      uncontrolledValue: defaultSelectedDate,
      isControlled: 'selectedDate' in props,
      isUncontrolled: 'defaultSelectedDate' in props,
      onChange: (newDate) => {
        if (newDate !== undefined) {
          onDateChange?.(newDate);
        }
      },
      componentName: 'DateTimeBlock.Root',
      controlledPropName: 'selectedDate',
      uncontrolledPropName: 'defaultSelectedDate',
    });

    const [selectedTimeState, setSelectedTimeState] = useControllableValue<
      Date | string | Array<Date | string> | undefined
    >({
      controlledValue: selectedTime,
      uncontrolledValue: defaultSelectedTime,
      isControlled: 'selectedTime' in props,
      isUncontrolled: 'defaultSelectedTime' in props,
      onChange: (newTime) => {
        if (newTime !== undefined) {
          onTimeChange?.(newTime);
        }
      },
      componentName: 'DateTimeBlock.Root',
      controlledPropName: 'selectedTime',
      uncontrolledPropName: 'defaultSelectedTime',
    });

    const effectiveDateSelectQuantityType = datesToShow ?? 3;

    /*
     * FormField context for accessibility
     */
    const controlId = useId();
    const labelId = useId();
    const descriptionId = useId();

    // Track which components are rendered for smart primary control detection
    const [hasTimesComponent, setHasTimesComponent] = useState(false);
    const [hasDatesComponent, setHasDatesComponent] = useState(false);

    // Detect if user is on a touch-screen device
    const isTouchScreen = useMediaQuery('(hover: none) and (pointer: coarse)');

    /*
     * The times "Select a date to see available times" help text is the only
     * element that mounts with descriptionId, and it only renders while no
     * date is selected. Once a date is selected the help text unmounts, so
     * unset descriptionId on the FormField context to prevent descendants
     * (e.g. DropdownSelect's inner <input>, which auto-applies it as
     * aria-describedby) from pointing at a non-existent id.
     */
    const publishedDescriptionId = selectedDateState ? undefined : descriptionId;

    const formFieldContextValue = useMemo(
      () => ({
        controlId,
        labelId,
        descriptionId: publishedDescriptionId,
        // Ensure we have required FormField props for proper Label behavior
        required: false,
        optional: false,
        disabled: false,
        error: false,
        readOnly: false,
      }),
      [controlId, labelId, publishedDescriptionId],
    );

    const contextValue = useMemo(
      () => ({
        dates,
        datesToShow: effectiveDateSelectQuantityType,
        disabledDates: disabledDates || [],
        disabledTimes: disabledTimes || [],
        formFieldContext: formFieldContextValue,
        hasDatesComponent,
        hasTimesComponent,
        includeOffset: Boolean(
          dates && dates.length > effectiveDateSelectQuantityType && !isTouchScreen,
        ),
        isTouchScreen,
        label,
        multiple,
        onDateChange: setSelectedDateState,
        onTimeChange: setSelectedTimeState,
        selectedDate: selectedDateState,
        selectedTime: selectedTimeState,
        setHasDatesComponent,
        setHasTimesComponent,
        timeDisplay,
        times: times || [],
        touchScreenOffset,
      }),
      [
        dates,
        disabledDates,
        disabledTimes,
        timeDisplay,
        effectiveDateSelectQuantityType,
        formFieldContextValue,
        hasDatesComponent,
        hasTimesComponent,
        label,
        multiple,
        selectedDateState,
        selectedTimeState,
        setHasDatesComponent,
        setHasTimesComponent,
        setSelectedDateState,
        setSelectedTimeState,
        times,
        isTouchScreen,
        touchScreenOffset,
      ],
    );

    return (
      <FormFieldContext.Provider value={formFieldContextValue}>
        <DateTimeBlockRootContext.Provider value={contextValue}>
          <div
            ref={forwardedRef}
            data-c11n-component="DateTimeBlock.Root"
            data-c11n-version={__C11N_VERSION__}
            className={cx('c11n', css(cssProp), className)}
            {...rest}
          >
            {children}
          </div>
        </DateTimeBlockRootContext.Provider>
      </FormFieldContext.Provider>
    );
  },
);

DateTimeBlockRoot.displayName = 'DateTimeBlock.Root';
